/** @prettier */
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TestView from "../View/TestView";
import ReviewQuestion from "./ReviewQuestion";
import ActionBar, { ActionBarItem } from "../View/ActionBar";
import {
    getCurrentQuestion,
    isFiltered,
    questionResult,
    TestReviewDispatch,
    TestReviewLoadedState,
    useTestReviewState,
} from "./testReviewState";
import useTestReviewKeyboardShortcuts from "./useTestReviewKeyboardShortcuts";
import { useHistory } from "react-router-dom";
import TestReviewSummaryBar from "./TestReviewSummaryBar";
import "./TestReview.css";
import { TestReviewTitleBar } from "./TestReviewTitleBar";

// Handles loading spinner state
type TestReviewWrapperProps = {
    id: string;
    // Initial filter to use
    filter?: string;
    // Initial question to show
    number?: number;
};
const TestReviewWrapper = ({ id, filter, number }: TestReviewWrapperProps) => {
    const [state, dispatch] = useTestReviewState(id, filter, number);
    return state.state === "loaded" ? (
        <TestReview id={id} state={state} dispatch={dispatch} />
    ) : (
        <div className="test-view">
            <div className="tr-loading">
                <FontAwesomeIcon icon="spinner" size="4x" spin />
            </div>
        </div>
    );
};
export default TestReviewWrapper;

type TestReviewProps = {
    id: string;
    state: TestReviewLoadedState;
    dispatch: TestReviewDispatch;
};
const TestReview = ({ id, state, dispatch }: TestReviewProps) => {
    useTestReviewKeyboardShortcuts(dispatch);
    const { currentQuestionIndex: questionNumber, totalQuestionCount } = state;
    const currentQuestion = getCurrentQuestion(state);

    return (
        <TestView
            id="test-review"
            titleBar={<TestReviewTitleBar state={state} dispatch={dispatch} />}
            percent={(questionNumber + 1) / totalQuestionCount}
            actionBar={<TestReviewActionBar testId={id} dispatch={dispatch} />}
            summaryBar={
                <TestReviewSummaryBar
                    dispatch={dispatch}
                    questionNumber={questionNumber}
                    summaryQuestions={state.questions.map((q, i) => ({
                        isFiltered: isFiltered(state, i),
                        isFlagged: q.flagged,
                        result: questionResult(q),
                    }))}
                />
            }
        >
            <ReviewQuestion
                questionNumber={questionNumber}
                question={currentQuestion!}
                totalQuestionCount={totalQuestionCount}
            />
        </TestView>
    );
};

const TestReviewActionBar = ({
    dispatch,
    testId,
}: {
    dispatch: TestReviewDispatch;
    testId: string;
}) => {
    const history = useHistory();
    return (
        <ActionBar>
            <ActionBarItem
                icon="step-backward"
                title="Return to previous question"
                onClick={() =>
                    dispatch({ event: "selectQuestion", index: "prev" })
                }
            />
            <ActionBarItem
                icon="step-forward"
                title="Go to next question"
                onClick={() =>
                    dispatch({ event: "selectQuestion", index: "next" })
                }
            />
            <ActionBarItem
                icon="chart-bar"
                title="View test results"
                onClick={() => history.push(`/tests/${testId}/results`)}
            />
        </ActionBar>
    );
};
